<template>
  <div>
    <div
      class="column-content-container"
      :style="{
        backgroundImage: `url(${configDataForm.backGroundImageFullUrl || noUpload})`,
        'background-repeat': 'no-repeat',
        'background-size': 'cover',
        'background-position': 'center',
      }"
      @mouseenter="handleShowOperationBtn(props.showEditContentButton)"
      @mouseleave="handleShowOperationBtn(false)"
    >
      <div class="left-content">
        <div class="title">
          <div class="title-icon">
            <img
              :src="leftIconImageUrl || noUpload"
              alt="栏目图标"
              loading="lazy"
            />
            <span class="title-text" :style="{ color: leftTitleColor }">{{
              leftTitle
            }}</span>
          </div>
          <div
            class="more"
            @click="handleMore(configDataForm.leftItem?.columnId)"
          >
            查看更多
          </div>
        </div>
        <div class="content">
          <div v-if="leftColumnContentList.length > 0" class="content-carousel">
            <el-carousel
              :interval="5000"
              :height="'100%'"
              arrow="always"
              @change="changeCarousel"
            >
              <el-carousel-item
                v-for="(item, index) in leftColumnContentList"
                :key="index"
                :name="item.coverImageName"
              >
                <a :href="item.url" target="_blank">
                  <el-image
                    style="width: 100%; height: 100%"
                    :src="item.coverImageUrl"
                    fit="cover"
                  >
                    <template #error>
                      <div class="image-error">
                        <el-icon :size="40"><Picture /></el-icon>
                      </div>
                    </template>
                  </el-image>
                </a>
              </el-carousel-item>
            </el-carousel>
          </div>
          <el-empty v-else description="暂无内容" :image-size="80" />
          <div
            v-if="carouselColumnContent && leftColumnContentList.length > 0"
            class="content-des"
            :style="{ background: configDataForm.backGroundColor }"
          >
            <div class="title-content">{{ carouselColumnContent.title }}</div>
            <div class="des-content">{{ carouselColumnContent.digest }}</div>
          </div>
        </div>
      </div>
      <div class="right-content">
        <div class="title">
          <div class="title-icon">
            <img
              :src="rightIconImageUrl || noUpload"
              alt="栏目图标"
              loading="lazy"
            />
            <span class="title-text" :style="{ color: rightTitleColor }">{{
              rightTitle
            }}</span>
          </div>
          <div
            class="more"
            @click="handleMore(configDataForm.rightItem?.columnId)"
          >
            查看更多
          </div>
        </div>
        <div class="content">
          <ul v-if="rightColumnContentList.length > 0">
            <li
              v-for="(item, index) in displayRightList"
              :key="item.id || index"
            >
              <a :href="item.url ? item.url : '#'" class="list-item">
                <div
                  class="item-title"
                  :style="{
                    color: item.fontColor,
                  }"
                >
                  {{ item.title }}
                </div>
                <div class="publish-time">
                  {{
                    item.publishTime
                      ? formatDate(item.publishTime)
                      : item.publishTime
                  }}
                </div>
              </a>
            </li>
          </ul>
          <el-empty v-else description="暂无内容" :image-size="60" />
        </div>
      </div>
      <operation-container
        v-if="showOperationBtn && !showEditForm"
        :component-data="props.componentData"
        :show-edit-content-button="props.showEditContentButton"
        :show-edit-components-button="props.showEditComponentsButton"
        :show-delete-components-button="props.showDeleteComponentsButton"
        :show-insert-components-button="props.showInsertComponentsButton"
        :show-sort-components-button="props.showSortComponentsButton"
        @handle-show-edit-form="handleShowEditForm"
        @handle-change-components="handleChangeComponents"
        @handle-delete-components="handleDeleteComponents"
        @handle-insert-components="handleInsertComponents"
        @handle-sort-components="handleSortComponents"
      ></operation-container>
    </div>
    <el-drawer
      v-if="showEditForm"
      v-model="showEditForm"
      size="60%"
      :title="props.componentData.componentName"
    >
      <column-content-edit-form
        v-if="showEditForm"
        v-model="configDataForm"
        :component-data="props.componentData"
        @handle-show-edit-form="handleShowEditForm"
        @handle-edit-components-content="handleEditComponentsContent"
      ></column-content-edit-form>
    </el-drawer>
  </div>
</template>

<script setup>
// #region 引用
import { Picture } from '@element-plus/icons-vue';
import { formatDate } from '@/utils/common/date-util';
import OperationContainer from '../common/OperationContainer.vue';
import ColumnContentEditForm from './ColumnContentEditFormSix.vue';

// #endregion

// #region props/emit
/**
 * 属性
 */
const props = defineProps({
  showEditContentButton: {
    type: Boolean,
    default: true,
  },
  showEditComponentsButton: {
    type: Boolean,
    default: false,
  },
  showDeleteComponentsButton: {
    type: Boolean,
    default: false,
  },
  showInsertComponentsButton: {
    type: Boolean,
    default: false,
  },
  showSortComponentsButton: {
    type: Boolean,
    default: false,
  },
  componentData: {
    type: Object,
    default() {},
  },
});

/**
 * 方法
 */
const emits = defineEmits([
  'handle-show-edit-form',
  'handle-change-components',
  'handle-delete-components',
  'handle-insert-components',
  'handle-sort-components',
  'handle-edit-components-content',
  'update:modelValue',
]);

// #endregion

// #region 变量/常量

// 处理图片未上传的情况
// 预加载所有图片（构建时生成映射）
const images = import.meta.glob('@/assets/images/web/common/other/*.png', {
  eager: true,
  query: '?url',
});
// 直接获取目标图片 URL
const noUpload =
  images['/src/assets/images/web/common/other/no-upload.png']?.default;

// 操作显示按钮控制
const showOperationBtn = ref(false);
// 显示编辑表单
const showEditForm = ref(false);
// 创建变量设默认值
const configDataForm = ref({});
if (props.componentData.configData) {
  configDataForm.value = JSON.parse(props.componentData.configData);
}
// 左侧图标
const leftIconImageUrl = computed(() => {
  return configDataForm.value.leftItem
    ? configDataForm.value.leftItem.iconFullUrl
    : '';
});

const leftTitle = computed(() => {
  return configDataForm.value.leftItem
    ? configDataForm.value.leftItem.title
    : '';
});

const leftTitleColor = computed(() => {
  return configDataForm.value.leftItem
    ? configDataForm.value.leftItem.fontColor
    : '';
});
// 左栏目内容
const leftColumnContentList = computed(() => {
  return configDataForm.value.leftItem
    ? configDataForm.value.leftItem.materialInfo
    : [];
});
const carouselColumn = ref(0);
const carouselColumnContent = computed(() => {
  return leftColumnContentList.value[carouselColumn.value];
});

// 右边侧图标
const rightIconImageUrl = computed(() => {
  return configDataForm.value.rightItem
    ? configDataForm.value.rightItem.iconFullUrl
    : '';
});

// 右边侧标题
const rightTitle = computed(() => {
  return configDataForm.value.rightItem
    ? configDataForm.value.rightItem.title
    : '';
});

const rightTitleColor = computed(() => {
  return configDataForm.value.rightItem
    ? configDataForm.value.rightItem.fontColor
    : '';
});
// 右边栏目内容
const rightColumnContentList = computed(() => {
  return configDataForm.value.rightItem
    ? configDataForm.value.rightItem.materialInfo
    : [];
});

// 移动端右侧列表显示数量限制
const isMobile = ref(false);
const displayRightList = computed(() => {
  if (isMobile.value && rightColumnContentList.value.length > 6) {
    return rightColumnContentList.value.slice(0, 6);
  }
  return rightColumnContentList.value;
});

// #endregion

// #region 生命周期

onMounted(() => {
  // 检测是否移动端
  isMobile.value = window.innerWidth <= 1180;
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 1180;
  });
});

// #endregion

// #region 业务方法

const changeCarousel = (index) => {
  carouselColumn.value = index;
};

/**
 * 查看更多
 */
const handleMore = (columnId) => {
  if (columnId) {
    window.location.href = `/column/${columnId}`;
  }
};

/**
 * 显示
 */
const handleShowOperationBtn = (flag) => {
  showOperationBtn.value = flag;
};

/**
 * 显示编辑表单
 */
const handleShowEditForm = (flag) => {
  showEditForm.value = flag;
};

/**
 * 改变组件
 */
const handleChangeComponents = () => {
  emits('handle-change-components', true);
};

/**
 * 删除组件
 */
const handleDeleteComponents = () => {
  emits('handle-delete-components', true);
};

/**
 * 插入组件
 */
const handleInsertComponents = () => {
  emits('handle-insert-components');
};
/**
 * 组件排序
 */
const handleSortComponents = (moveObj) => {
  emits('handle-sort-components', moveObj);
};
/**
 * 处理编辑表单内容
 */
const handleEditComponentsContent = (flag) => {
  // 待数据更新完成后才执行
  nextTick(() => {
    emits('update:modelValue', configDataForm.value);
    emits('handle-edit-components-content', true);
  });

  handleShowEditForm(flag);
};

// #endregion
</script>
<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

// ============================================
// 栏目内容组件变量（继承官网设计系统）
// ============================================
$column-color-primary: $website-primary; // 主色
$column-color-accent: $website-primary-light; // 强调色
$column-color-text: $website-text-primary; // 文字色
$column-color-text-secondary: $website-text-secondary; // 次要文字
$column-color-text-muted: $website-text-muted; // 弱化文字
$column-color-border: $website-border-light; // 边框色

// 动画时长
$column-duration-normal: $website-duration-normal;

// 动画曲线
$column-ease-out: $website-ease-out;

// 圆角（继承设计系统）
$column-radius-sm: $website-radius-sm; // 4px
$column-radius-md: $website-radius-md; // 8px
$column-radius-lg: $website-radius-lg; // 12px
$column-radius-full: $website-radius-full; // 999px

/* PC端样式 */
@include pc {
  .column-content-container {
    position: relative;
    display: flex;
    width: 100%;
    height: 560px;
    padding: 0 80px;
    margin: 50px 0;

    .left-content {
      display: flex;
      flex-direction: column;
      width: 45%;
      height: 100%;
      padding-right: 20px;

      .title {
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding-bottom: 20px;
        border-bottom: 1px solid $column-color-border;

        .title-icon {
          display: flex;
          align-content: center;
          align-items: center;
          font-size: 36px;

          img {
            width: 36px;
            height: 36px;
            margin-right: 10px;
            object-fit: cover;
            transition: transform $column-duration-normal $column-ease-out;
          }

          &:hover img {
            transform: scale(1.1) rotate(5deg);
          }

          .title-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .more {
          display: flex;
          align-items: center;
          font-size: 20px;
          color: $column-color-text-muted;
          cursor: pointer;
          transition: all $column-duration-normal $column-ease-out;

          &:hover {
            color: $column-color-primary;
            transform: translateX(3px);
          }
        }
      }

      .content {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        align-items: center;
        padding-top: 40px;

        .content-carousel {
          flex-grow: 1;
          width: 100%;
          overflow: hidden;
          border-radius: $column-radius-lg $column-radius-lg 0 0;

          .el-carousel {
            position: relative;
            width: 100%;
            height: 100%;
            margin: auto;
            overflow: hidden;
            border-radius: $column-radius-lg $column-radius-lg 0 0;
          }

          .el-carousel__item:nth-child(2n) {
            background-color: #f0f2f5;
          }

          .el-carousel__item:nth-child(2n + 1) {
            background-color: #e8eaed;
          }

          // 轮播箭头优化
          :deep(.el-carousel__arrow) {
            width: 40px;
            height: 40px;
            background: rgb(0 0 0 / 30%);
            backdrop-filter: blur(4px);
            border-radius: 50%;
            transition: all $column-duration-normal $column-ease-out;

            &:hover {
              background: rgb(25 66 148 / 80%);
              transform: scale(1.1);
            }
          }

          // 轮播指示器优化
          :deep(.el-carousel__indicators) {
            display: flex;
            justify-content: center;
            padding: 10px 0;
          }

          :deep(.el-carousel__indicator) {
            margin: 0 6px;

            .el-carousel__button {
              width: 8px;
              height: 8px;
              background: rgb(255 255 255 / 50%);
              border-radius: 50%;
              transition: all $column-duration-normal $column-ease-out;
            }

            &.is-active .el-carousel__button {
              width: 24px;
              background: $column-color-accent;
              border-radius: 4px;
            }
          }

          // Ken Burns 动效
          :deep(.el-carousel__item) {
            .el-image {
              transition: transform 8s ease-out;
            }

            &.is-active .el-image {
              animation: kenBurns 8s ease-out forwards;
            }
          }
        }

        .content-des {
          display: flex;
          flex-direction: column;
          justify-content: space-evenly;
          width: 100%;
          height: 180px;
          padding: 20px 40px;
          color: #fff;
          border-radius: 0 0 $column-radius-md $column-radius-md;

          .title-content {
            font-size: 24px;
            font-weight: bold;
          }

          .des-content {
            display: -webkit-box;
            overflow: hidden;
            font-size: 18px;
            color: #b9c5de;
            text-overflow: ellipsis;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }
      }
    }

    .right-content {
      display: flex;
      flex-direction: column;
      width: 55%;
      height: 100%;
      padding-left: 20px;

      .title {
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding-bottom: 20px;
        border-bottom: 1px solid $column-color-border;

        .title-icon {
          display: flex;
          align-content: center;
          align-items: center;
          font-size: 36px;

          img {
            width: 36px;
            height: 36px;
            margin-right: 10px;
            object-fit: cover;
            transition: transform $column-duration-normal $column-ease-out;
          }

          &:hover img {
            transform: scale(1.1) rotate(5deg);
          }

          .title-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .more {
          display: flex;
          align-items: center;
          font-size: 20px;
          color: $column-color-text-muted;
          cursor: pointer;
          transition: all $column-duration-normal $column-ease-out;

          &:hover {
            color: $column-color-primary;
            transform: translateX(3px);
          }
        }
      }

      .content {
        flex-grow: 1;
        padding-top: 20px;

        ul {
          width: 100%;

          li {
            position: relative;
            height: 60px;
            padding-left: 40px;
            border-bottom: 1px solid $column-color-border;
            transition: all $column-duration-normal $column-ease-out;

            // 装饰点（使用渐变圆点替代点号）
            &::before {
              position: absolute;
              top: 50%;
              left: 10px;
              width: 8px;
              height: 8px;
              content: '';
              background: linear-gradient(
                135deg,
                $column-color-primary,
                $column-color-accent
              );
              border-radius: 50%;
              transition: transform $column-duration-normal $column-ease-out;
              transform: translateY(-50%);
            }

            // hover 时列表项效果
            &:hover {
              background: rgba($column-color-primary, 0.03);
              transform: translateX(5px);

              &::before {
                transform: translateY(-50%) scale(1.3);
              }
            }

            .list-item {
              display: flex;
              flex-direction: row;
              align-items: center;
              height: inherit;

              .item-title {
                flex-grow: 1;
                overflow: hidden;
                font-size: 18px;
                color: $column-color-text;
                text-overflow: ellipsis;
                white-space: nowrap;
                transition: color $column-duration-normal $column-ease-out;
              }

              .publish-time {
                width: 120px;
                margin-left: auto;
                overflow: hidden;
                font-size: 18px;
                color: $column-color-text-muted;
                text-align: right;
                white-space: nowrap;
              }

              &:hover .item-title {
                color: $column-color-primary;
              }
            }
          }
        }
      }
    }
  }
}

// 图片错误样式
.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #909399;
  background: #f5f7fa;
  border-radius: $website-radius-md;
}
</style>

<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

/* 移动端样式 */
@include mobile {
  .column-content-container {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    padding: 4vw;
    background-color: #fff;
    // background-image: none !important;

    .left-content {
      display: flex;
      flex-direction: column;
      align-content: center;

      .title {
        display: flex;
        justify-content: space-between;
        width: 92vw;
        padding-bottom: 4vw;

        // [灵动-2026-03-10] 标题图标脉动发光
        .title-icon {
          display: flex;
          align-content: center;
          align-items: center;
          font-size: 7.5vw;

          img {
            width: 7.5vw;
            height: 7.5vw;
            margin-right: 2vw;
            object-fit: cover;
            animation: iconGlow 2s ease-in-out infinite;
          }

          .title-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .more {
          display: flex;
          align-items: center;
          font-size: 4vw;
          color: $website-text-muted;
          cursor: pointer;
          transition:
            color 0.25s ease-out,
            transform 0.25s ease-out;

          // [灵动-2026-03-10] 触摸缩放
          &:active {
            color: $website-primary;
            transform: scale(0.95);
          }
        }
      }

      .content {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        align-items: center;
        padding-top: 4vw;

        .content-carousel {
          flex-grow: 1;
          width: 92vw;
          height: 56.25vw;
          min-height: 220px;
          max-height: 360px;
          overflow: hidden;
          border-radius: $website-radius-lg $website-radius-lg 0 0;

          .el-carousel {
            position: relative;
            width: 92vw;
            height: 100%;
            margin: auto;
            overflow: hidden;
            border-radius: $website-radius-lg $website-radius-lg 0 0;
          }

          .el-carousel__item:nth-child(2n) {
            background-color: #f0f2f5;
          }

          .el-carousel__item:nth-child(2n + 1) {
            background-color: #e8eaed;
          }

          // 移动端轮播箭头优化
          :deep(.el-carousel__arrow) {
            width: 30px;
            height: 30px;
            background: rgb(0 0 0 / 30%);
            backdrop-filter: blur(4px);
            border-radius: 50%;

            .el-icon {
              font-size: 16px;
            }
          }

          // 移动端指示器优化 - 防止换行
          :deep(.el-carousel__indicators) {
            display: flex;
            justify-content: center;
            max-width: 85%;
            padding: 2vw 0;
            margin: 0 auto;
            overflow-x: auto;
            scrollbar-width: none;

            &::-webkit-scrollbar {
              display: none;
            }
          }

          :deep(.el-carousel__indicator) {
            flex-shrink: 0;
            margin: 0 1vw;

            .el-carousel__button {
              width: 2vw;
              min-width: 6px;
              height: 2vw;
              min-height: 6px;
              background: rgb(255 255 255 / 50%);
              border-radius: 50%;
              transition: all 0.25s ease-out;
            }

            &.is-active .el-carousel__button {
              width: 4vw;
              min-width: 12px;
              background: $website-primary-light;
              border-radius: 1vw;
            }
          }
        }

        .content-des {
          display: flex;
          flex-direction: column;
          justify-content: space-evenly;
          width: 92vw;
          height: 28vw;
          min-height: 100px;
          padding: 4vw;
          color: #fff;
          border-radius: 0 0 $website-radius-md $website-radius-md;

          .title-content {
            overflow: hidden;
            font-size: 5vw;
            font-weight: bold;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .des-content {
            display: -webkit-box;
            overflow: hidden;
            font-size: 4vw;
            color: #b9c5de;
            text-overflow: ellipsis;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }
      }
    }

    .right-content {
      display: flex;
      flex-direction: column;
      height: auto;
      padding-top: 4vw;

      .title {
        display: flex;
        justify-content: space-between;
        width: 92vw;
        padding-bottom: 4vw;

        .title-icon {
          display: flex;
          align-content: center;
          align-items: center;
          font-size: 7.5vw;

          img {
            width: 7.5vw;
            height: 7.5vw;
            margin-right: 2vw;
            object-fit: cover;
            // [灵动-2026-03-11] 标题图标脉动发光（与左边一致）
            animation: iconGlow 2s ease-in-out infinite;
          }

          .title-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .more {
          display: flex;
          align-items: center;
          font-size: 4vw;
          color: $website-text-muted;
          cursor: pointer;
          transition:
            color 0.25s ease-out,
            transform 0.25s ease-out;

          // [灵动-2026-03-11] 触摸缩放（与左边一致）
          &:active {
            color: $website-primary;
            transform: scale(0.95);
          }
        }
      }

      .content {
        flex-grow: 1;
        background-color: #f2f5f9;

        ul {
          margin: 0 3vw;

          li {
            position: relative;
            height: 12vw;
            min-height: 44px;
            padding-left: 4vw;
            border-bottom: 0.2vw solid $website-border-light;
            // [灵动-2026-03-12] 入场动画 + 错落延迟（使用 mixin）
            @include entrance-animation(slideInRight, 0.35s);
            @include stagger-delay(6, 0.05s);

            transition:
              background 0.25s ease-out,
              transform 0.25s ease-out;

            // 装饰点（移动端适配）- [灵动-2026-03-10] 呼吸动画
            &::before {
              position: absolute;
              top: 50%;
              left: 1vw;
              width: 2vw;
              min-width: 6px;
              height: 2vw;
              min-height: 6px;
              content: '';
              background: linear-gradient(
                135deg,
                $website-primary,
                $website-primary-light
              );
              border-radius: 50%;
              transform: translateY(-50%);
              animation: dotPulseVertical 1.5s ease-in-out infinite;
            }

            // [灵动-2026-03-10] 触摸滑动 + 装饰点放大
            &:active {
              background: rgba($website-primary, 0.05);
              transform: translateX(2vw);

              &::before {
                transform: translateY(-50%) scale(1.3);
                animation: none;
              }
            }

            .list-item {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              height: inherit;

              .item-title {
                width: 58vw;
                overflow: hidden;
                font-size: 4vw;
                color: $website-text-primary;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              .publish-time {
                width: 21vw;
                overflow: hidden;
                font-size: 3.75vw;
                color: $website-text-muted;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }
  }

  // 移动端图片错误样式
  .image-error {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #909399;
    background: #f5f7fa;
    border-radius: $website-radius-md;

    .el-icon {
      width: 10vw;
      height: 10vw;
    }
  }
}
</style>
